import { CSSTransition, TransitionGroup } from "react-transition-group";
import { useFormStore } from "../../Stores/Form";
import { AppMode, useRouterStore } from "../../Stores/Router";
import FormItem from "../FormItem/FormItem";
import FormLoading from "../FormLoading/FormLoading";
import FormProgressBar from "../FormProgressBar/FormProgressBar";
import FormSendButton from "../FormSendButton/FormSendButton";
import { useConfigStore } from "../../Stores/Config";
import "./Form.scss";

function InlineFormError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  const configStore = useConfigStore();
  return (
    <div className="form-error form-error--inline" role="alert">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M12 7v6M12 16.5v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      {/* Hosts pass technical errors (status codes, JSON); show a plain line. */}
      <span className="form-error-text" title={message}>
        {configStore.translateTextWithFallback(
          "surveySendFailed",
          "Your answers couldn’t be sent.",
        )}
      </span>
      <button type="button" className="form-error-retry" onClick={onRetry}>
        {configStore.translateTextWithFallback("tryAgain", "Try again")}
      </button>
    </div>
  );
}

function Form({ animated = true }: { animated?: boolean }) {
  const formStore = useFormStore();
  const routerStore = useRouterStore();
  const currentAction = formStore.action;
  const hasChatStyle = formStore?.action?.chatStyle ?? false;
  const canShowIntro =
    routerStore.appMode !== AppMode.SURVEY &&
    routerStore.appMode !== AppMode.SURVEY_FULL &&
    routerStore.appMode !== AppMode.SURVEY_WEB;

  // Surveys keep the form on screen while sending (the button spins) and show
  // a failed submit inline above it, so the answers stay put for a retry.
  const surveyMode = !canShowIntro;

  if (
    !formStore.action ||
    !formStore.action.form ||
    (formStore.sendingForm && !surveyMode) ||
    formStore.checkingDuplicates
  ) {
    return (
      <div className="form-loading">
        <FormLoading />
      </div>
    );
  }

  if (formStore.formSent) {
    return (
      <div className="form-sent">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
          <circle cx="26" cy="26" r="25" fill="none"></circle>
          <path fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"></path>
        </svg>
        <div className="form-sent-label">{formStore.action.thanksMessage}</div>
      </div>
    );
  }

  const renderError = () => {
    if (surveyMode) {
      return null;
    }
    if (formStore.formError) {
      return <div className="form-error">{formStore.formError}</div>;
    }

    return null;
  };

  const renderFormItems = () => {
    return formStore.getCurrentFormItems().map((formItem: any) => {
      return (
        <CSSTransition
          key={formItem.name}
          classNames={`form-item-animation`}
          timeout={700}
        >
          <FormItem formItem={formItem} key={formItem.name} />
        </CSSTransition>
      );
    });
  };

  const renderFormContent = () => {
    return (
      <>
        {renderFormItems()}
        {surveyMode && formStore.formError && !formStore.sendingForm && (
          <InlineFormError
            message={formStore.formError}
            onRetry={() => formStore.nextAction()}
          />
        )}
        <FormSendButton />
      </>
    );
  };

  return (
    <>
      {renderError()}
      <div className="form-container-main">
        <FormProgressBar />
        {canShowIntro &&
          !hasChatStyle &&
          formStore.currentStep === 0 &&
          currentAction &&
          typeof currentAction.introMessage === "string" &&
          currentAction.introMessage.trim() && (
            <div className="form-intro">{currentAction.introMessage}</div>
          )}
        {animated ? (
          <TransitionGroup
            className={`form-container-animation--${formStore.navigationDirection}`}
          >
            {renderFormContent()}
          </TransitionGroup>
        ) : (
          renderFormContent()
        )}
      </div>
    </>
  );
}

export default Form;
